/* eslint-disable camelcase */
import api, { BaseResponse, apiV2 } from '../lib/baseAxios';

import {
  FileType,
  IFolderInput,
  ISharedFileDetails,
  GlobalSearchQuery,
  SortBy,
  SortOrderBy,
  Pagination,
  Segment,
  FolderType,
  Speaker,
  SharePermission,
  TimeStampedLine,
} from '../types';
import { MoveFileData } from '../features/selectedVideo/selectedVideoSlice';
import {
  FolderActionsParams,
  FolderActionsType,
} from '../features/library/librarySlice';
import { ProfileType, ContentType } from '../constants/mediaFormatProfiles';

export interface IVFSResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface FetchFilesParams {
  teamId?: string;
  folderId?: string;
  ownerId?: string;
  sortBy?: SortBy;
  sortOrder?: SortOrderBy;
  page?: number;
  limit?: number;
  searchQuery?: string;
}

export interface FetchFileSystemResponseBody {
  fileSystem: FileType[];
  fileSystemVersion: number;
  librarySize: number;
  pagination: Pagination;
  freeUserTimeLimit: Date | null;
}

export const fetchFileSystem = async (
  params: FetchFilesParams | undefined = {}
) => {
  return apiV2.get<IVFSResponse<FetchFileSystemResponseBody>>(`/files`, {
    params,
  });
};

export const createFolder = async (details: IFolderInput) => {
  return api.post(`/fs/folders`, details);
};

interface CreateNewFolderArgs {
  teamId: string;
  name: string;
}
interface CreateNewFolderResponse {
  folder: FolderType;
}

export const createNewFolder = async ({
  teamId,
  name,
}: CreateNewFolderArgs) => {
  return apiV2.post<IVFSResponse<CreateNewFolderResponse>>(
    `/folders/${teamId}`,
    {
      name,
    }
  );
};

export const updateFolder = async (
  id: string,
  changes: {
    name?: string;
    parentId?: string;
    teamId?: string;
    prompt?: string;
  }
) => {
  return api.post(`/fs/folders/${id}`, changes);
};

export const renameFolder = (id: string, name: string, teamId: string) =>
  updateFolder(id, { name, teamId });

export const moveFolder = (id: string, parentId: string) =>
  updateFolder(id, { parentId });

export const editPrompt = (id: string, teamId: string, prompt: string) =>
  updateFolder(id, { teamId, prompt });

export const deleteFolder = async (id: string) => {
  return api.delete(`/fs/folders/${id}`);
};

export const deleteTeamSpaceFolder = async (id: string) => {
  return api.delete(`/fs/team-space/folders/${id}`);
};

// @deprecated
export const getSharedItems = async (
  shareId: string
): Promise<BaseResponse<ISharedFileDetails>> => {
  return api.get(`/fs/share/${shareId}`);
};

interface ShareFileV2Params {
  fileId: string;
  permissions?: SharePermission[];
}

interface ShareFileResponseData {
  shareId: string;
}
export const shareFile = async ({ fileId, permissions }: ShareFileV2Params) => {
  const response = await apiV2.post<IVFSResponse<ShareFileResponseData>>(
    `/files/actions/${fileId}/share`,
    {
      permissions,
    }
  );
  const shareId = response?.data.data.shareId;
  return { shareId };
};

export const unshareFile = async (fileId: string) => {
  return apiV2.delete<IVFSResponse<{}>>(`/files/actions/${fileId}/share`);
};

// Used by the public share view page. Not by logged in users
export const getSharedFile = async (shareId: string) => {
  const response = await apiV2.get<IVFSResponse<GetFileReturnType>>(
    `/files/shared/${shareId}`
  );
  return response?.data?.data;
};

export interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface PromptResponses {
  [key: string]: {
    promptTitle: string;
    responseText: string;
  };
}

export interface GetFileReturnType {
  file: FileType;
  jobs: {
    transcribe?: {
      recordingId: string;
      position: number;
      timeToComplete: number;
      updatedAt: string;
    };
  };
  transcript: {
    compact_segments?: Segment[];
    program_version: string;
    file_version: number;
  };
  chat: {
    sessionId: string;
    suggestions: string[];
    conversation: ConversationMessage[];
  };
  postProcess: {
    systemPromptResponses: PromptResponses;
    userPromptResponses: PromptResponses;
  };
}
export const getFile = async (fileId: string) => {
  return apiV2.get<IVFSResponse<GetFileReturnType>>(`/files/${fileId}`);
};

export const updateFile = async <T extends string = 'file' | 'folder'>(
  id: string,
  changes: {
    name?: string;
    parentId?: string | null;
    description?: string;
    ownerId?: string;
    teamId?: string;
    spaceId?: string;
    selectedTeamId?: string;
  }
): Promise<IVFSResponse<{ [key in T]: FileType }>> => {
  return api.post(`/fs/files/${id}`, changes);
};

export const updateFile2 = async (
  id: string,
  changes: {
    name?: string;
    description?: string;
  }
) => {
  return apiV2.post<IVFSResponse<{ file: FileType }>>(`/files/${id}`, changes);
};

export const moveFile = (id: string, args: { [x: string]: any }) =>
  updateFile(id, args);

export const moveManyFiles = async (fileIds: string[], folderId: string) => {
  return api.post(`/fs/files/move`, { fileIds, folderId });
};

export const deleteFile = async (teamId: string, id: string) => {
  return api.delete(`/fs/files/${teamId}/${id}`);
};

export const uploadThumbnail = async (id: string, data: FormData) => {
  const _config = {
    headers: {
      'content-type': 'multipart/form-data',
    },
  };
  return api.post(`/fs/thumb/upload/${id}`, data, _config);
};

export const transcribe = ({
  id,
  ownerId,
  teamId,
}: {
  id: string;
  teamId: string | undefined;
  ownerId: string | undefined;
}) => {
  if (teamId) {
    return api.post(
      `/fs/files/${id}/transcript?teamId=${teamId}&ownerId=${ownerId}`
    );
  }

  return api.post(`/fs/files/${id}/transcript`);
};

export interface UpdateSpeakerProps {
  teamId: string;
  fileId: string;
  speakerId: string;
  speaker: {
    name: string;
    picture?: string;
    userId?: string;
  };
}

export interface UpdateSpeakerResponseData {
  speakers: {
    [key: string]: Speaker;
  };
}

export const updateSpeaker = async (data: UpdateSpeakerProps) => {
  const { speaker, fileId, speakerId } = data;
  const response = await apiV2.post<IVFSResponse<UpdateSpeakerResponseData>>(
    `/files/actions/${fileId}/speaker/${speakerId}`,
    speaker
  );
  return response?.data?.data?.speakers;
};

// Not in use
export const statusCheck = async (id: string) => {
  // TODO: Remove this endpoint once socket is implemented
  return api.get(`fs/files/status-check/${id}`);
};

export const addDuration = async ({
  id,
  duration,
}: {
  id: string;
  duration: number;
}) => {
  return api.post(`/fs/files/${id}/duration`, { duration });
};

export const transcode = async ({
  id,
  profile,
  ownerId,
  teamId,
}: {
  id: string;
  profile: string;
  teamId: string | undefined;
  ownerId: string | undefined;
}) => {
  if (teamId) {
    return api.post(
      `/fs/files/${id}/transcode?profile=${profile}&teamId=${teamId}&ownerId=${ownerId}`
    );
  }
  return api.post(`/fs/files/${id}/transcode?profile=${profile}`);
};

interface GetDownloadUrlResponseBody {
  url: string;
}
export const getDownloadUrl = async ({
  fileId,
  profile,
}: {
  fileId: string;
  profile: ProfileType;
}): Promise<GetDownloadUrlResponseBody> => {
  const response = await apiV2.post<IVFSResponse<GetDownloadUrlResponseBody>>(
    `/files/actions/${fileId}/download/${profile}`
  );
  return response.data.data;
};

export const summarize = async ({
  id,
  force = true,
  ownerId,
  teamId,
}: {
  id: string;
  teamId: string | undefined;
  ownerId: string | undefined;
  force?: boolean;
}) => {
  if (teamId) {
    return api.post(
      `/fs/files/${id}/summary?force=${force}&teamId=${teamId}&ownerId=${ownerId}`
    );
  }
  return api.post(`/fs/files/${id}/summary?force=${force}`);
};

export const chromeExtension = async () => {
  return api.get(`/settings/chrome-extension`);
};

export const deleteManyFiles = async (fileIds: string[]) => {
  return api.post(`/fs/files/delete`, { fileIds });
};

export const getSettings = async () => {
  return api.get(`/fs/settings`);
};

export const updateSettings = async (settings: { cloudSync: boolean }) => {
  return api.post(`/fs/settings`, settings);
};

export const fetchAISuggestions = async ({
  id,
  teamId,
  ownerId,
}: {
  id: string;
  teamId: string;
  ownerId: string;
}) => {
  if (teamId) {
    return api.get(
      `/fs/files/${id}/ask/suggestions?teamId=${teamId}&ownerId=${ownerId}`
    );
  }
  return api.get(`/fs/files/${id}/ask/suggestions`);
};

// @deprecated
export const askAI = async ({
  continuation,
  id,
  teamId,
  ownerId,
  sessionId,
}: {
  id: string;
  continuation: { role: 'assistant' | 'user'; content: string }[];
  teamId: string;
  ownerId: string;
  sessionId: string;
}) => {
  if (teamId) {
    return api.post(`/fs/files/${id}/ask?teamId=${teamId}&ownerId=${ownerId}`, {
      continuation,
      sessionId,
    });
  }
  return api.post(`/fs/files/${id}/ask`, { continuation, sessionId });
};

interface AskAIArgs {
  fileId: string;
  question: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  typing?: boolean;
}

interface AskAIResponse {
  sessionId: string;
  answer: ChatMessage;
}
export const askAIV2 = async ({ fileId, question }: AskAIArgs) => {
  return apiV2.post<IVFSResponse<AskAIResponse>>(`/files/${fileId}/ask`, {
    question,
  });
};

export const deleteAskAISessionV2 = async (
  fileId: string,
  sessionId: string
) => {
  return apiV2.delete(`/files/${fileId}/ask/${sessionId}`);
};

export const deleteTemplateOutputV2 = async (
  fileId: string,
  templateId: string
) => {
  return apiV2.delete(
    `/files/actions/${fileId}/template-outputs/${templateId}`
  );
};

export interface SaveEnhancedNotesParams {
  fileId: string;
  responseText: string;
}

export const saveEnhancedNotes = async ({
  fileId,
  responseText,
}: SaveEnhancedNotesParams) => {
  return apiV2.put<IVFSResponse<{ success: boolean }>>(
    `/files/actions/${fileId}/template-outputs/system/ENHANCED_NOTES`,
    {
      responseText,
    }
  );
};

export interface SaveNotesParams {
  fileId: string;
  notes: TimeStampedLine[];
}

export const saveNotes = async ({ fileId, notes }: SaveNotesParams) => {
  return apiV2.put<IVFSResponse<{ success: boolean }>>(
    `/files/${fileId}/notes`,
    { notes }
  );
};

export interface FolderActionsResponse {
  folders: FolderType[];
  folderAction: FolderActionsType;
}

export const folderActionsV2 = async ({
  folderId: fileId,
  action,
  data,
}: FolderActionsParams) => {
  return apiV2.post<IVFSResponse<FolderActionsResponse>>(
    `/folders/${fileId}/${action}`,
    {
      data,
    }
  );
};

export interface GetFoldersParams {
  teamId: string;
}

export interface GetFoldersResponse {
  folders: FolderType[];
}

export const getFolders = async ({ teamId }: GetFoldersParams) => {
  return apiV2.get<IVFSResponse<GetFoldersResponse>>(`/folders/${teamId}`);
};

export const getImportFileDetails = async (data: []) => {
  return api.post(`/fs/file-import-details`, data, { responseType: 'blob' });
};

export const recordingCount = async () => {
  return api.post(`/fs/recording/count`);
};

export const getRecordingCount = async () => {
  return api.get(`/fs/recording/count`);
};

export const searchRequest = (data: GlobalSearchQuery) => {
  return api.post('fs/global-search', data);
};

interface TrimRemoteData {
  begin: number;
  end: number;
  replace?: boolean;
  ownerId?: string;
  spaceFolderId?: string;
  teamId?: string;
}
// @deprecated
export const trimRemote = async (id: string, data: TrimRemoteData) => {
  return api.post(`/trim/${id}`, data);
};
export interface TrimParams {
  fileId: string;
  startMS: number;
  endMS: number;
}
export const trim = async ({
  fileId,
  startMS,
  endMS,
}: TrimParams): Promise<
  IVFSResponse<{
    data: {
      file: FileType;
    };
    success: boolean;
  }>
> => {
  return apiV2.delete(`/files/actions/${fileId}/trim`, {
    params: { startMS, endMS },
  });
};

export const moveFile2 = async ({
  fileId,
  toTeamId,
  toFolderId,
}: MoveFileData): Promise<
  IVFSResponse<{
    data: any;
    success: boolean;
  }>
> => {
  return apiV2.post(`/files/actions/${fileId}/move`, { toTeamId, toFolderId });
};

export const deleteFile2 = async (
  fileId: string
): Promise<
  IVFSResponse<{
    data: any;
    success: boolean;
  }>
> => {
  return apiV2.delete(`/files/actions/${fileId}/delete`);
};

interface GetUploadUrlsOptions {
  teamId: string;
  folderId: string;
  files: { name: string; contentType: ContentType }[];
}
interface GetUploadUrlsResponse {
  uploadParams: { fileId: string; uploadUrl: string }[];
}
export const getUploadUrls = async ({
  teamId,
  folderId,
  files,
}: GetUploadUrlsOptions) => {
  return apiV2.post<IVFSResponse<GetUploadUrlsResponse>>(
    `/files/upload/urls/${teamId}/${folderId}/`,
    {
      files,
    }
  );
};

interface ImportUrlResponseBody {
  file: FileType;
}

export const importFile = async (
  teamId: string,
  folderId: string,
  url: string,
  options: {} // Not in use atm
) => {
  return apiV2.post<IVFSResponse<ImportUrlResponseBody>>(
    `/files/import/${teamId}/${folderId}`,
    { url, options }
  );
};

interface FinalizeUploadOptions {
  teamId: string;
  folderId: string;
  fileId: string;
  contentType: string;
  name: string;
  description?: string;
}

interface FinalizeUploadResponseBody {
  file: FileType;
}

export const finalizeUploads = async ({
  teamId,
  folderId,
  ...file
}: FinalizeUploadOptions) => {
  return apiV2.post<IVFSResponse<FinalizeUploadResponseBody>>(
    `/files/upload/finalize/${teamId}/${folderId}`,
    { file }
  );
};

type ActionType = 'reprocess';

const action = (
  actionType: ActionType,
  fileId: string
): Promise<
  IVFSResponse<{
    file: FileType;
  }>
> => api.post(`/actions/${actionType}`, { fileId });

export const reprocess = async (fileId: string) => {
  return action('reprocess', fileId);
};

interface ReprocessV2ResponseBody {
  success: boolean;
  message: string;
  file: FileType;
}

export const reprocessV2 = async (fileId: string) => {
  const response = await apiV2.post<IVFSResponse<ReprocessV2ResponseBody>>(
    `/files/actions/${fileId}/reprocess`
  );
  return response.data;
};

export interface SaveTemplateOutputParams {
  fileId: string;
  templateId: string;
  responseText: string;
  promptTitle?: string;
}

export const saveTemplateOutput = async ({
  fileId,
  templateId,
  responseText,
  promptTitle,
}: SaveTemplateOutputParams) => {
  return apiV2.put<IVFSResponse<{ success: boolean }>>(
    `/files/actions/${fileId}/template-outputs/${templateId}`,
    {
      responseText,
      promptTitle,
    }
  );
};

export interface GetThumbnailByTimestampParams {
  fileId: string;
  timestamp: number;
}

interface GetThumbnailByTimestampResponseBody {
  url: string;
}

export const getThumbnailByTimestamp = async ({
  fileId,
  timestamp,
}: GetThumbnailByTimestampParams) => {
  const response = await apiV2.post<
    IVFSResponse<GetThumbnailByTimestampResponseBody>
  >(`/files/${fileId}/thumb/${timestamp}`, {
    timestamp,
  });
  return response.data.data.url;
};
